import type { NotificationIconName } from './notification-icons';

export type NotificationTone = 'primary' | 'success' | 'warning' | 'danger' | 'info' | 'neutral';

/** Icon-chip classes per tone (bg + icon color + border), light + dark. */
export const TONE_ICON_CLASSES: Record<NotificationTone, string> = {
    primary:
        'bg-primary-50 text-primary-600 border-primary-100 dark:bg-primary-900/40 dark:text-primary-300 dark:border-transparent',
    success:
        'bg-success-50 text-success-600 border-success-100 dark:bg-success-900/40 dark:text-success-300 dark:border-transparent',
    warning:
        'bg-warning-50 text-warning-600 border-warning-100 dark:bg-warning-900/40 dark:text-warning-300 dark:border-transparent',
    danger: 'bg-error-50 text-error-600 border-error-100 dark:bg-error-900/40 dark:text-error-300 dark:border-transparent',
    info: 'bg-info-50 text-info-600 border-info-100 dark:bg-info-900/40 dark:text-info-300 dark:border-transparent',
    neutral:
        'bg-neutral-100 text-neutral-600 border-neutral-200 dark:bg-bg-raised dark:text-neutral-400 dark:border-transparent',
};

/**
 * Subtle row tint for UNREAD notifications, per tone. Background only — rows are
 * separated by a shared divider now, so a per-row border would double it up.
 */
export const TONE_ROW_UNREAD_CLASSES: Record<NotificationTone, string> = {
    primary: 'bg-primary-50/50 dark:bg-primary-900/15',
    success: 'bg-success-50/50 dark:bg-success-900/15',
    warning: 'bg-warning-50/50 dark:bg-warning-900/15',
    danger: 'bg-error-50/50 dark:bg-error-900/15',
    info: 'bg-info-50/50 dark:bg-info-900/15',
    neutral: 'bg-muted/40',
};

/**
 * The vertical bar down the left edge of an unread row — the unread marker that
 * survives at a glance where a pale tint alone does not, and the one cue that
 * still works for a reader who cannot separate the tint from white.
 */
export const TONE_ACCENT_BAR_CLASSES: Record<NotificationTone, string> = {
    primary: 'bg-primary-500',
    success: 'bg-success-500',
    warning: 'bg-warning-500',
    danger: 'bg-error-500',
    info: 'bg-info-500',
    neutral: 'bg-neutral-400',
};

export interface ICategoryVisual {
    /**
     * A glyph NAME, not a component — `NotificationIcon` resolves it against
     * `NOTIFICATION_ICONS`. Keeping this a plain string is what lets the map
     * below stay a data table with no icon imports of its own.
     */
    readonly icon: NotificationIconName;
    readonly tone: NotificationTone;
}

/**
 * Per-event icon + tone. Keyed by the `NotificationCategory` enum string the
 * backend sends. Adding a category is a one-line change; unknown categories
 * fall back to a neutral bell.
 *
 * Glyphs are Lucide — one family across the whole bell AND the same family as
 * the rest of the app, so no row is drawn in a different hand from the one
 * above it or from the chrome around it. Names are resolved to components by
 * `notification-icons`.
 *
 * Every category in the API's `NOTIFICATION_CATEGORY_CONFIG` is listed. That is
 * the point of the exercise: a category with no entry renders the grey fallback
 * bell, which is indistinguishable from an event this app has never heard of —
 * and it is how a customer asking for a human once read as background noise.
 */
const CATEGORY_VISUALS: Record<string, ICategoryVisual> = {
    // ── MESSAGE ──────────────────────────────────────────────────────────
    INBOUND_MESSAGE: { icon: 'MessageCircleMore', tone: 'info' },
    // A reply arrow rather than a second speech bubble: this sits directly
    // above/below INBOUND_MESSAGE in the same tab, and two bubbles in the same
    // blue chip are one glance away from being the same row.
    CAMPAIGN_REPLY: { icon: 'CornerUpLeft', tone: 'info' },
    CONVERSATION_ASSIGNED: { icon: 'UserPlus', tone: 'primary' },
    CONVERSATION_NOTE_MENTION: { icon: 'AtSign', tone: 'primary' },
    // The snooze the user set has run out and the conversation is back in
    // their queue — news about their own reminder, not a problem.
    CONVERSATION_SNOOZE_ENDED: { icon: 'AlarmClock', tone: 'info' },
    // ── ALERT — campaigns ────────────────────────────────────────────────
    CAMPAIGN_SCHEDULED: { icon: 'CalendarPlus', tone: 'info' },
    CAMPAIGN_STARTED: { icon: 'Send', tone: 'primary' },
    CAMPAIGN_PAUSED: { icon: 'CirclePause', tone: 'warning' },
    CAMPAIGN_RESUMED: { icon: 'CirclePlay', tone: 'primary' },
    CAMPAIGN_COMPLETED: { icon: 'CircleCheck', tone: 'success' },
    CAMPAIGN_FAILED: { icon: 'CircleX', tone: 'danger' },
    // A/B tests: the question is the one that needs a person, so it is the only
    // one of the three wearing an urgency tone.
    CAMPAIGN_EXPERIMENT_APPROVAL_NEEDED: { icon: 'BadgeQuestionMark', tone: 'warning' },
    CAMPAIGN_EXPERIMENT_DECIDED: { icon: 'FlaskConical', tone: 'primary' },
    CAMPAIGN_EXPERIMENT_WINNER_SENT: { icon: 'Trophy', tone: 'success' },
    // ── ALERT — contacts / segments ──────────────────────────────────────
    CSV_IMPORT_COMPLETED: { icon: 'FileSpreadsheet', tone: 'success' },
    CSV_IMPORT_FAILED: { icon: 'FileX', tone: 'danger' },
    SEGMENT_CREATED: { icon: 'Users', tone: 'primary' },
    SEGMENT_UPDATED: { icon: 'PenLine', tone: 'info' },
    BULK_OPERATION_COMPLETED: { icon: 'ListChecks', tone: 'success' },
    // ── ALERT — polls ────────────────────────────────────────────────────
    POLL_ACTIVATED: { icon: 'ChartColumn', tone: 'primary' },
    POLL_EXPIRED: { icon: 'Timer', tone: 'warning' },
    POLL_CLOSED: { icon: 'Lock', tone: 'neutral' },
    // ── ALERT — users / org ──────────────────────────────────────────────
    USER_INVITED: { icon: 'Mail', tone: 'info' },
    USER_JOINED: { icon: 'UserCheck', tone: 'success' },
    USER_ROLE_CHANGED: { icon: 'ShieldCheck', tone: 'primary' },
    // ── ALERT — channels / providers ─────────────────────────────────────
    CHANNEL_CONNECTED: { icon: 'Plug', tone: 'success' },
    CHANNEL_DISCONNECTED: { icon: 'Unplug', tone: 'warning' },
    CHANNEL_ERROR: { icon: 'TriangleAlert', tone: 'danger' },
    PROVIDER_ERROR: { icon: 'Server', tone: 'danger' },
    // ── ALERT — knowledge base ───────────────────────────────────────────
    KB_SOURCE_INGESTED: { icon: 'BookOpenText', tone: 'success' },
    // ── ALERT — AI agents ────────────────────────────────────────────────
    // `warning` because a handover is actionable-now but not a failure;
    // suspension is `danger` because the AI has stopped for good.
    AI_HANDOVER_REQUESTED: { icon: 'Hand', tone: 'warning' },
    AI_SUSPENDED: { icon: 'Bot', tone: 'danger' },
    // ── ALERT — voice ────────────────────────────────────────────────────
    MISSED_CALL: { icon: 'PhoneMissed', tone: 'warning' },
    VOICEMAIL_RECEIVED: { icon: 'Voicemail', tone: 'info' },
    // ── ALERT — scheduling ───────────────────────────────────────────────
    MEETING_BOOKED: { icon: 'CalendarCheck', tone: 'success' },
    MEETING_CANCELLED: { icon: 'CalendarX', tone: 'danger' },
    // Not a third calendar glyph: booked/cancelled/rescheduled would then be
    // three near-identical squares, and rescheduled is the one whose meaning is
    // the change itself rather than the date.
    MEETING_RESCHEDULED: { icon: 'RefreshCw', tone: 'warning' },
    // A lead the service area turned away: actionable (call them), not a failure.
    OUT_OF_AREA_REQUEST: { icon: 'MapPinOff', tone: 'warning' },
    // ── ALERT — calendar integrations ────────────────────────────────────
    CALENDAR_CONNECTION_BROKEN: { icon: 'Unlink', tone: 'danger' },
    CALENDAR_EVENT_DESYNCED: { icon: 'CalendarOff', tone: 'warning' },
    // ── ALERT — workflows ────────────────────────────────────────────────
    WORKFLOW_ALERT: { icon: 'Workflow', tone: 'danger' },
    // ── ALERT — advertising integrations ─────────────────────────────────
    AD_CONNECTION_BROKEN: { icon: 'Megaphone', tone: 'danger' },
    // ── ALERT — lead forms ───────────────────────────────────────────────
    LEAD_FORM_SUBMISSION: { icon: 'ClipboardList', tone: 'primary' },
    // ── ALERT — bug reports ──────────────────────────────────────────────
    // Refined per report type by `BUG_REPORT_TYPE_VISUALS` below; this entry is
    // what a notification whose metadata carries no type falls back to. `info`
    // rather than an urgency tone: it is news, not a problem — and the grey
    // fallback bell made it read as system chatter, which is the one thing a
    // human reply addressed to you is not.
    BUG_REPORT_UPDATED: { icon: 'LifeBuoy', tone: 'info' },
    // ── ALERT — opportunities ────────────────────────────────────────────
    OPPORTUNITY_ASSIGNED: { icon: 'UserPlus', tone: 'primary' },
    // Same glyph and tone as CONVERSATION_NOTE_MENTION: being mentioned is the
    // same event wherever the note was written.
    OPPORTUNITY_COMMENT_MENTION: { icon: 'AtSign', tone: 'primary' },
    OPPORTUNITY_TASK_ASSIGNED: { icon: 'ListChecks', tone: 'info' },
    OPPORTUNITY_WON: { icon: 'CircleCheck', tone: 'success' },
};

/**
 * A bug-report notification carries the report's type in `metadata.reportType`,
 * so the alert wears the same glyph the report itself does in "My reports" — a
 * beetle on a bug, a bulb on an idea, a headset on a support request. One
 * category covers all three, so without this every reply looked identical.
 *
 * Tones follow the glyphs: a defect reads `danger`, an idea `warning` (amber,
 * the bulb's own colour), a question `info`.
 *
 * The keys mirror the API's `BugReportType` enum. An unrecognised value — an
 * older notification, or a type added on the portal before this ships — falls
 * back to the category default rather than rendering nothing.
 */
const BUG_REPORT_TYPE_VISUALS: Record<string, ICategoryVisual> = {
    BUG: { icon: 'Bug', tone: 'danger' },
    FEATURE: { icon: 'Lightbulb', tone: 'warning' },
    SUPPORT: { icon: 'Headset', tone: 'info' },
};

/**
 * An inbound message's glyph follows the CHANNEL it arrived on, which the
 * notification already carries in `metadata.channel`.
 *
 * One category covers every channel, so an email reply wore the same speech
 * bubble as a text — and the bell is exactly where you decide which inbox to
 * open, so that one glance was the whole point of the row.
 *
 * Only EMAIL differs. SMS, WhatsApp and web chat are all chat surfaces a bubble
 * reads correctly for, and they keep the category glyph unchanged. The tone is
 * deliberately identical to `INBOUND_MESSAGE`'s, so only the glyph moves — the
 * chip colour that groups these rows together stays put.
 *
 * An absent or unrecognised channel falls through to the category default, so
 * a notification stored before this shipped renders exactly as it did before.
 */
const INBOUND_MESSAGE_CHANNEL_VISUALS: Record<string, ICategoryVisual> = {
    EMAIL: { icon: 'Mail', tone: 'info' },
};

const FALLBACK_VISUAL: ICategoryVisual = { icon: 'Bell', tone: 'neutral' };

/**
 * Resolve the icon + tone for one notification. `metadata` is optional: callers
 * that only have a category still get the category-level visual, which is what
 * every non-bug-report notification uses anyway.
 */
export function getNotificationVisual(
    category: string,
    metadata?: Record<string, unknown> | null,
): ICategoryVisual {
    if (category === 'BUG_REPORT_UPDATED') {
        const reportType = metadata?.['reportType'];
        if (typeof reportType === 'string' && BUG_REPORT_TYPE_VISUALS[reportType]) {
            return BUG_REPORT_TYPE_VISUALS[reportType];
        }
    }
    if (category === 'INBOUND_MESSAGE') {
        const channel = metadata?.['channel'];
        if (typeof channel === 'string' && INBOUND_MESSAGE_CHANNEL_VISUALS[channel]) {
            return INBOUND_MESSAGE_CHANNEL_VISUALS[channel];
        }
    }
    return CATEGORY_VISUALS[category] ?? FALLBACK_VISUAL;
}
